import { useTranslation } from "react-i18next";
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Table, Pagination } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'react';
import { useMemo, useState } from 'react';
import EmptyComponent from '../../empty';
import "./CustomTransferStudentTable.scss";

interface RecordType {
  key: string;
  name: string;
  username: string;
}

interface Props {
  data: RecordType[];
  selectedKeys: string[];
  selectedData: RecordType[];
  onChange: (keys: string[], selectedRecords: RecordType[]) => void;
  onLeftPageChange?: (page: number, size: number) => void;
  leftPagination?: { total: number; current: number; pageSize: number };
  loading?: boolean;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const CustomTransferStudentTable = ({
  data,
  selectedKeys,
  selectedData,
  onChange,
  onLeftPageChange,
  leftPagination = { total: data.length, current: 1, pageSize: 20 },
  loading
}: Props) => {
  const { t } = useTranslation();
  const [selectedLeftKeys, setSelectedLeftKeys] = useState<string[]>([]);
  const [selectedLeftRowsMap, setSelectedLeftRowsMap] = useState<Map<string, RecordType>>(new Map());
  const [selectedRightKeys, setSelectedRightKeys] = useState<string[]>([]);
  const [rightCurrent, setRightCurrent] = useState(1);
  const [rightPageSize, setRightPageSize] = useState(20);

  const leftData = useMemo(() => data.filter(item => !selectedKeys.includes(item.key)), [data, selectedKeys]);
  const rightData = useMemo(() => selectedData, [selectedData]);

  const handleAdd = () => {
    const addedKeys = Array.from(selectedLeftRowsMap.keys());
    const addedData = Array.from(selectedLeftRowsMap.values());
    const newKeys = Array.from(new Set([...selectedKeys, ...addedKeys]));
    const map = new Map([...selectedData, ...addedData].map(i => [i.key, i]));
    onChange(newKeys, Array.from(map.values()));
    setSelectedLeftKeys([]);
    setSelectedLeftRowsMap(new Map());
  };

  const handleRemove = () => {
    const newKeys = selectedKeys.filter(key => !selectedRightKeys.includes(key));
    const newData = selectedData.filter(item => !selectedRightKeys.includes(item.key));
    onChange(newKeys, newData);
    setSelectedRightKeys([]);
  };

  const columns: ColumnsType<RecordType> = [
    {
      title: t("Học viên", "Học viên"),
      dataIndex: 'name',
      render: (_: any, record) => (
        <>
          <div className='student-name'>{record.name}</div>
          <div>({record.username})</div>
        </>
      ),
    },
  ];

  const pagedRightData = useMemo(
    () => rightData.slice((rightCurrent - 1) * rightPageSize, rightCurrent * rightPageSize),
    [rightData, rightCurrent, rightPageSize]
  );

  return (
    <div className='custom-transfer-student-table'>
      <div className='transfer-student-table'>
        <div className='total-student-transfer'>{t("Tổng số học viên:", "Tổng số học viên:")} {leftPagination.total}</div>
        <Table
          rowKey="key"
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedLeftKeys,
            onChange: (selectedRowKeys: Key[], selectedRows: RecordType[]) => {
              const currentKeys = selectedRowKeys.map(String);
              const updatedMap = new Map(selectedLeftRowsMap);
              data.forEach(record => {
                if (currentKeys.includes(record.key)) {
                  updatedMap.set(record.key, record);
                } else {
                  updatedMap.delete(record.key);
                }
              });
              setSelectedLeftRowsMap(updatedMap);
              setSelectedLeftKeys(Array.from(updatedMap.keys()));
            },
            getCheckboxProps: (record) => ({
              disabled: selectedKeys.includes(record.key)
            })
          }}
          columns={columns}
          scroll={leftPagination.total === 0 ? undefined : { x: 100, y: 470 }}
          dataSource={leftData}
          pagination={false}
          locale={{
            emptyText: <EmptyComponent description={t("Không có kết quả", "Không có kết quả")} />,
          }}
          loading={loading}
        />
        {leftPagination.total > 0 ?
          <Pagination
            simple
            current={leftPagination.current}
            pageSize={leftPagination.pageSize}
            total={leftPagination.total}
            onChange={(page, pageSize) => {
              const newPage = pageSize !== leftPagination.pageSize ? 1 : page;
              onLeftPageChange?.(newPage, pageSize!);
            }}
            style={{ marginTop: 24, textAlign: 'right' }}
            locale={{
              items_per_page: "/ trang",
              jump_to: t("Đi đến trang", "Đi đến trang"),
              page: "",
            }}
            showSizeChanger
          /> : null}

      </div>

      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Button
          style={{ width: "24px", height: "24px" }}
          icon={<RightOutlined />}
          onClick={handleAdd}
          disabled={!selectedLeftKeys.length}
        />
        <Button
          style={{ width: "24px", height: "24px", marginTop: 8 }}
          icon={<LeftOutlined />}
          onClick={handleRemove}
          disabled={!selectedRightKeys.length}
        />
      </div>

      <div className='transfer-student-table'>
        <div className='total-student-transfer'>{t("Học viên đã chọn:", "Học viên đã chọn:")} {selectedKeys?.length}</div>
        <Table
          rowKey="key"
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedRightKeys,
            onChange: (selectedRowKeys: Key[]) => setSelectedRightKeys(selectedRowKeys.map(String))
          }}
          columns={columns}
          scroll={rightData.length === 0 ? undefined : { x: 100, y: 470 }}
          dataSource={pagedRightData}
          pagination={false}
          locale={{
            emptyText: <EmptyComponent description={t("Không có kết quả", "Không có kết quả")} />,
          }}
        />
        {rightData.length > 0 ?
          <Pagination
            simple
            current={rightCurrent}
            pageSize={rightPageSize}
            total={rightData.length}
            onChange={(page, pageSize) => {
              const newPage = pageSize !== rightPageSize ? 1 : page;
              setRightCurrent(newPage);
              setRightPageSize(pageSize!);
            }}
            style={{ marginTop: 24, textAlign: 'right' }}
            locale={{
              items_per_page: "/ trang",
              jump_to: t("Đi đến trang", "Đi đến trang"),
              page: "",
            }}
            showSizeChanger
          /> : null}

      </div>
    </div>
  );
};

export default CustomTransferStudentTable;
